/**
 * Abnormal-Balance Detector — Phase polish.
 *
 * Surfaces accounts whose period or all-time balance is on the WRONG side
 * of their normal balance (e.g. revenue with a debit balance, AR with a
 * credit balance, cash overdrawn). These are nearly always either:
 *   • A misposting (wrong account)
 *   • A legitimate edge case (returns, prepayments) that warrants review
 *   • A reversal that overshot the original
 *
 * Pure selector. No coupling to the provider so it imports cleanly anywhere.
 */
import type { AccountBalance, GLAccount } from "../glTypes";

export type AbnormalSeverity = "info" | "warn" | "critical";

export interface AbnormalBalanceRow {
  accountId: string;
  accountNumber: string;
  accountName: string;
  expectedSide: "debit" | "credit";
  /** Absolute dollar amount on the wrong side. */
  amount: number;
  severity: AbnormalSeverity;
  reason: string;
}

/**
 * `tb.net` is computed using normal-balance convention (positive = on the
 * expected side). A NEGATIVE net therefore means the balance sits on the
 * abnormal side — that's our trigger.
 */
export function getAbnormalBalances(
  trialBalance: AccountBalance[],
  accounts: GLAccount[],
): AbnormalBalanceRow[] {
  const accById = new Map(accounts.map(a => [a.id, a]));
  const out: AbnormalBalanceRow[] = [];
  for (const tb of trialBalance) {
    const acc = accById.get(tb.accountId);
    if (!acc || !acc.active) continue;
    // 1 cent floor — rounding noise should not generate findings.
    if (tb.net >= -0.005) continue;
    const amount = Math.abs(tb.net);
    const severity: AbnormalSeverity =
      acc.subtype === "cash" || acc.subtype === "accounts_receivable" || acc.subtype === "accounts_payable"
        ? "critical"
        : amount >= 1000 ? "warn" : "info";
    const reason =
      acc.subtype === "cash" ? "Cash overdrawn — verify bank rec and outstanding items."
      : acc.subtype === "accounts_receivable" ? "AR shows a credit balance — likely overpayment or unapplied credit."
      : acc.subtype === "accounts_payable" ? "AP shows a debit balance — likely vendor prepayment or over-pay."
      : acc.type === "revenue" ? "Revenue account has a debit balance — check for refund or misposting."
      : acc.type === "expense" ? "Expense account has a credit balance — check for reversal or refund."
      : acc.type === "liability" ? "Liability sits on the asset side — review postings."
      : "Balance on the unexpected side. Review activity.";
    out.push({
      accountId: acc.id,
      accountNumber: acc.number,
      accountName: acc.name,
      expectedSide: acc.normalBalance,
      amount,
      severity,
      reason,
    });
  }
  return out.sort((a, b) => {
    const rank = { critical: 0, warn: 1, info: 2 } as const;
    return rank[a.severity] - rank[b.severity] || b.amount - a.amount;
  });
}

/** Inactivation guard — refuse to deactivate an account with a non-zero net. */
export function canInactivateAccount(
  accountId: string,
  trialBalance: AccountBalance[],
): { ok: boolean; reason?: string; balance?: number } {
  const tb = trialBalance.find(t => t.accountId === accountId);
  if (!tb) return { ok: true };
  const net = Math.round(tb.net * 100);
  if (net !== 0) {
    return {
      ok: false,
      reason: `Account has a non-zero balance of $${Math.abs(tb.net).toFixed(2)}. Close to retained earnings or move balances first.`,
      balance: tb.net,
    };
  }
  return { ok: true };
}
